"use client";

import { useEffect } from "react";
import { useMerchant } from "@/app/providers/merchantProvider";
import { useMerchantFeatureFlag } from "@/utils/useMerchantFeatureFlag";
import {
  FEATURE_FLAG_QUANTUM_METRIC,
  MERCHANT_PLAN_BY_BUSINESS_TYPE,
  MERCHANT_PLAN_UNKNOWN,
} from "@/utils/constants";
import { Merchant } from "@/dto/merchant";

const SCRIPT_TAG_ID = "js-quantum-metric-clover";

// Prefix for temporary dev diagnostics so the QuantumMetric event flow can be
// traced in the browser console on the dev environment.
const QM_LOG_PREFIX = "[QuantumMetrics]";

// QuantumMetric custom event that tags the session with the merchant whose
// storefront it belongs to. Event ID 11 / value 0 are provisioned in
// QuantumMetric; the third argument carries the merchant UUID as the event's
// string payload. See QuantumMetricAPI.sendEvent(eventId, value, data).
const QM_MERCHANT_UUID_EVENT_ID = 11;
const QM_MERCHANT_UUID_EVENT_VALUE = 0;

// QuantumMetric custom event that tags the session with the merchant's plan,
// i.e. its business type (restaurant/service/retail/hybrid/essential). Event ID
// 6 / value 0 are provisioned in QuantumMetric; the third argument carries the
// derived plan label as the event's string payload.
const QM_MERCHANT_PLAN_EVENT_ID = 6;
const QM_MERCHANT_PLAN_EVENT_VALUE = 0;

interface QuantumMetricAPI {
  // eslint-disable-next-line no-unused-vars
  sendEvent: (eventId: number, value: number, data: string) => void;
}

declare global {
  interface Window {
    QuantumMetricAPI?: QuantumMetricAPI;
  }
}

// Derives the merchant's "plan" label from its business type for the
// QuantumMetric plan event. A hybrid merchant carries business type SERVICE, so
// it maps to the Services Growth plan. Returns MERCHANT_PLAN_UNKNOWN when the
// merchant is missing or its business type has no mapping.
function deriveMerchantPlan(merchant: Merchant | undefined): string {
  if (!merchant) return MERCHANT_PLAN_UNKNOWN;

  return (
    MERCHANT_PLAN_BY_BUSINESS_TYPE[merchant.merchantBusinessType] ??
    MERCHANT_PLAN_UNKNOWN
  );
}

export function QuantumMetrics() {
  const { merchant } = useMerchant();
  const isQuantumMetricEnabled = useMerchantFeatureFlag(
    merchant,
    FEATURE_FLAG_QUANTUM_METRIC
  );

  const merchantUuid = merchant?.id;
  const merchantPlan = deriveMerchantPlan(merchant);

  useEffect(() => {
    if (!isQuantumMetricEnabled) {
      console.info(`${QM_LOG_PREFIX} feature flag disabled — skipping`);
      return;
    }

    // The QuantumMetric vendor script calls crypto.randomUUID() during init,
    // which only exists in a secure context (HTTPS or http://localhost). On dev
    // deployments served over plain HTTP with a real hostname the context is
    // insecure and crypto.randomUUID is undefined, so the script throws and
    // never exposes QuantumMetricAPI. Skip QuantumMetric there rather than
    // polyfilling; in prod (HTTPS) this guard is a no-op.
    if (typeof globalThis.crypto?.randomUUID !== "function") {
      console.warn(
        `${QM_LOG_PREFIX} insecure context (plain HTTP) — skipping QuantumMetric; crypto.randomUUID is unavailable`
      );
      return;
    }

    console.info(
      `${QM_LOG_PREFIX} feature flag enabled for merchant UUID:`,
      merchantUuid
    );

    // Records the merchant UUID and plan for the session once the QuantumMetric
    // script has loaded and exposed its API. Returns true when the events were
    // sent.
    const sendMerchantEvents = (): boolean => {
      if (!merchantUuid) {
        console.warn(`${QM_LOG_PREFIX} no merchant UUID — cannot send event`);
        return false;
      }

      const quantumMetricAPI = window.QuantumMetricAPI;

      if (quantumMetricAPI) {
        quantumMetricAPI.sendEvent(
          QM_MERCHANT_UUID_EVENT_ID,
          QM_MERCHANT_UUID_EVENT_VALUE,
          merchantUuid
        );

        quantumMetricAPI.sendEvent(
          QM_MERCHANT_PLAN_EVENT_ID,
          QM_MERCHANT_PLAN_EVENT_VALUE,
          merchantPlan
        );

        console.info(
          `${QM_LOG_PREFIX} sent sendEvent(${QM_MERCHANT_UUID_EVENT_ID}, ${QM_MERCHANT_UUID_EVENT_VALUE}, "${merchantUuid}") and sendEvent(${QM_MERCHANT_PLAN_EVENT_ID}, ${QM_MERCHANT_PLAN_EVENT_VALUE}, "${merchantPlan}")`
        );

        return true;
      }

      return false;
    };

    if (document.head.querySelector(`script#${SCRIPT_TAG_ID}`)) {
      // Script tag already present from a prior render — the API should already
      // be exposed, so send immediately.
      console.info(`${QM_LOG_PREFIX} QM script already present`);
      sendMerchantEvents();
    } else {
      const script = document.createElement("script");

      script.src =
        process.env.NEXT_PUBLIC_QUANTUM_METRIC_SCRIPT_URL ||
        "https://cdn.quantummetric.com/qscripts/quantum-clover-test.js";

      script.type = "text/javascript";
      script.async = true;
      script.id = SCRIPT_TAG_ID;

      // The script is async, so QuantumMetricAPI is not available until it has
      // finished loading. Defer sending the merchant events until the script's
      // load event fires; sending synchronously here would read an undefined
      // window.QuantumMetricAPI.
      script.addEventListener("load", () => {
        console.info(`${QM_LOG_PREFIX} QM script loaded`);
        sendMerchantEvents();
      });

      console.info(`${QM_LOG_PREFIX} injecting QM script:`, script.src);
      document.head.appendChild(script);
    }
  }, [isQuantumMetricEnabled, merchantUuid, merchantPlan]);

  return null;
}
